/**
 * Utilitários para validação de documentos (CPF e CNPJ)
 */

/**
 * Valida CPF
 * @param cpf String do CPF
 * @returns Boolean indicando se é válido
 */
export const validateCPF = (cpf: string): boolean => {
  const cleanCPF = cpf.replace(/\D/g, '');
  if (cleanCPF.length !== 11) return false;
  
  // Verificar se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(cleanCPF)) return false;
  
  // Validar primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.charAt(9))) return false;
  
  // Validar segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.charAt(10))) return false;
  
  return true;
};

/**
 * Valida CNPJ
 * @param cnpj String do CNPJ
 * @returns Boolean indicando se é válido
 */
export const validateCNPJ = (cnpj: string): boolean => {
  const cleanCNPJ = cnpj.replace(/\D/g, '');
  if (cleanCNPJ.length !== 14) return false;
  
  // Verificar se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(cleanCNPJ)) return false;
  
  // Validar primeiro dígito verificador
  let sum = 0;
  let weight = 5;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleanCNPJ.charAt(i)) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }
  let remainder = sum % 11;
  const firstDigit = remainder < 2 ? 0 : 11 - remainder;
  if (firstDigit !== parseInt(cleanCNPJ.charAt(12))) return false;
  
  // Validar segundo dígito verificador
  sum = 0;
  weight = 6;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleanCNPJ.charAt(i)) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }
  remainder = sum % 11;
  const secondDigit = remainder < 2 ? 0 : 11 - remainder;
  if (secondDigit !== parseInt(cleanCNPJ.charAt(13))) return false;
  
  return true;
};

/**
 * Formata CPF
 * @param cpf String do CPF
 * @returns String formatada
 */
export const formatCPF = (cpf: string): string => {
  const cleanCPF = cpf.replace(/\D/g, '');
  return cleanCPF.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

/**
 * Formata CNPJ
 * @param cnpj String do CNPJ
 * @returns String formatada
 */
export const formatCNPJ = (cnpj: string): string => {
  const cleanCNPJ = cnpj.replace(/\D/g, '');
  return cleanCNPJ.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

/**
 * Obtém o documento formatado baseado no tipo
 * @param tipoDocumento Tipo do documento
 * @param documento Número do documento
 * @returns String formatada
 */
export const getFormattedDocument = (tipoDocumento: 'cpf' | 'cnpj', documento: string): string => {
  if (tipoDocumento === 'cpf') {
    return formatCPF(documento);
  } else {
    return formatCNPJ(documento);
  }
};

/**
 * Obtém o label do documento baseado no tipo
 * @param tipoDocumento Tipo do documento
 * @returns String do label
 */
export const getDocumentLabel = (tipoDocumento: 'cpf' | 'cnpj'): string => {
  return tipoDocumento === 'cpf' ? 'CPF' : 'CNPJ';
};

/**
 * Aplica máscara de CPF
 * @param value Valor a ser formatado
 * @returns String formatada
 */
export const applyCPFMask = (value: string): string => {
  const cleanValue = value.replace(/\D/g, '');
  if (cleanValue.length <= 3) return cleanValue;
  if (cleanValue.length <= 6) return cleanValue.replace(/(\d{3})(\d+)/, '$1.$2');
  if (cleanValue.length <= 9) return cleanValue.replace(/(\d{3})(\d{3})(\d+)/, '$1.$2.$3');
  return cleanValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

/**
 * Aplica máscara de CNPJ
 * @param value Valor a ser formatado
 * @returns String formatada
 */
export const applyCNPJMask = (value: string): string => {
  const cleanValue = value.replace(/\D/g, '');
  if (cleanValue.length <= 2) return cleanValue;
  if (cleanValue.length <= 5) return cleanValue.replace(/(\d{2})(\d+)/, '$1.$2');
  if (cleanValue.length <= 8) return cleanValue.replace(/(\d{2})(\d{3})(\d+)/, '$1.$2.$3');
  if (cleanValue.length <= 12) return cleanValue.replace(/(\d{2})(\d{3})(\d{3})(\d+)/, '$1.$2.$3/$4');
  return cleanValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

/**
 * Detecta o tipo de documento baseado no número de dígitos
 * @param value Valor a ser analisado
 * @returns Tipo do documento ou null se não for possível detectar
 */
export const detectDocumentType = (value: string): 'cpf' | 'cnpj' | null => {
  const cleanValue = value.replace(/\D/g, '');
  if (cleanValue.length === 11) return 'cpf';
  if (cleanValue.length === 14) return 'cnpj';
  return null;
};

/**
 * Aplica máscara baseada no tipo de documento
 * @param value Valor a ser formatado
 * @param tipoDocumento Tipo do documento
 * @returns String formatada
 */
export const applyDocumentMask = (value: string, tipoDocumento: 'cpf' | 'cnpj'): string => {
  if (tipoDocumento === 'cpf') {
    return applyCPFMask(value);
  } else {
    return applyCNPJMask(value);
  }
};

/**
 * Limita o input baseado no tipo de documento
 * @param value Valor atual
 * @param tipoDocumento Tipo do documento
 * @returns String limitada
 */
export const limitDocumentInput = (value: string, tipoDocumento: 'cpf' | 'cnpj'): string => {
  const cleanValue = value.replace(/\D/g, '');
  if (tipoDocumento === 'cpf' && cleanValue.length > 11) {
    return cleanValue.substring(0, 11);
  }
  if (tipoDocumento === 'cnpj' && cleanValue.length > 14) {
    return cleanValue.substring(0, 14);
  }
  return cleanValue;
};
